// frontend/src/components/layout/PublicToolsLayout.tsx
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Box, Container, Group, Text, Button } from '@mantine/core';
import {
  IconFlask,
  IconCalendarEvent,
  IconLogin,
  IconTestPipe2,
  IconTools,
} from '@tabler/icons-react';

const TOOLS = [
  {
    label: 'Trace Element Calculator',
    path: '/tools/dosing-calculator',
    icon: IconFlask,
  },
  {
    label: 'HTTM Scheduler',
    path: '/tools/httm-scheduler',
    icon: IconCalendarEvent,
  },
  {
    label: 'Dose Calculator',
    path: '/tools/dose-calculator',
    icon: IconTestPipe2,
  },
];

export interface PublicToolsLayoutProps {
  /**
   * A share link's page (/s/:token): no tools row, and links out are full page
   * loads, so the share page never shares a document with Google Analytics.
   */
  sharePage?: boolean;
}

export function PublicToolsLayout({ sharePage = false }: PublicToolsLayoutProps): JSX.Element {
  const location = useLocation();

  return (
    <Box
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--mantine-color-gray-0)',
      }}
    >
      {/* Header - matches logged-in AppHeader style */}
      <Box
        style={{
          height: 60,
          backgroundColor: 'var(--rdh-header-bg)',
        }}
      >
        <Container size="lg" h="100%">
          <Group h="100%" justify="space-between">
            <Text size="lg" fw={700} c="var(--rdh-brand-accent)" style={{ letterSpacing: '0.5px' }}>
              Reef Data Hub
            </Text>
            <Button
              component={Link}
              to="/login"
              reloadDocument={sharePage}
              variant="subtle"
              color="teal"
              size="sm"
              leftSection={<IconLogin size={16} />}
            >
              Login
            </Button>
          </Group>
        </Container>
      </Box>

      {/* Tool Navigation */}
      <Box
        py="xs"
        display={sharePage ? 'none' : undefined}
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Container size="lg">
          <Group gap="xs" wrap="wrap">
            <Button
              component={Link}
              to="/tools"
              variant={location.pathname === '/tools' ? 'filled' : 'light'}
              color="teal"
              size="xs"
              leftSection={<IconTools size={14} />}
            >
              All Tools
            </Button>
            <Text size="sm" c="dimmed" fw={500} mr="xs">
              |
            </Text>
            {TOOLS.map((tool) => {
              const isActive = location.pathname === tool.path;
              const Icon = tool.icon;

              return (
                <Button
                  key={tool.path}
                  component={Link}
                  to={tool.path}
                  variant={isActive ? 'filled' : 'light'}
                  color="teal"
                  size="xs"
                  leftSection={<Icon size={14} />}
                >
                  {tool.label}
                </Button>
              );
            })}
          </Group>
        </Container>
      </Box>

      {/* Page Content */}
      <Outlet />
    </Box>
  );
}
